import { useQuery } from "@apollo/client/react";
import { USER_NOTIFICATIONS } from "@graphql/queries/UserNotifications";

import { useAuth } from "./use-auth";
import {
  applyBroadcastState,
  useBroadcastNotificationState,
} from "./use-broadcast-notification-state";
import { useWatchUserWsEvent } from "./use-watch-user-ws-event";
import type { NotificationUpdateEvent } from "../codegen/ws-schema";

interface UseVisibleNotificationsOptions {
  domain?: string;
  unreadOnly?: boolean;
  prune?: boolean;
}

interface NotificationLike {
  uuid: string;
  isBroadcast: boolean;
  readAt?: unknown;
  dismissedAt?: unknown;
  activeAt?: unknown;
  expiresAt?: unknown;
}

export function filterVisibleNotifications<
  T extends NotificationLike,
>(notifications: (T | null)[] | null | undefined): T[] {
  const now = Date.now();
  return (notifications ?? [])
    .filter((n): n is T => Boolean(n))
    .filter((n) => !n.dismissedAt)
    .filter(
      (n) =>
        !n.activeAt ||
        new Date(n.activeAt as string).getTime() <= now,
    )
    .filter(
      (n) =>
        !n.expiresAt ||
        new Date(n.expiresAt as string).getTime() > now,
    );
}

export function useVisibleNotifications({
  domain,
  unreadOnly,
  prune,
}: UseVisibleNotificationsOptions = {}) {
  const { isUserAuthenticated } = useAuth();
  const variables: Record<string, unknown> = {};
  if (domain !== undefined) variables.notificationDomain = domain;
  if (unreadOnly !== undefined) variables.unreadOnly = unreadOnly;

  const { data, loading, error, refetch } = useQuery(
    USER_NOTIFICATIONS,
    {
      variables,
      fetchPolicy: "cache-and-network",
      skip: !isUserAuthenticated,
    },
  );

  useWatchUserWsEvent({
    eventName: "notification_update",
    onMessage: (msg) => {
      const data = (msg as NotificationUpdateEvent).data;
      const eventDomain = data?.notification_domain;
      if (domain && eventDomain && eventDomain !== domain) return;
      const delay = Math.random() * (data?.jitter_ms_max ?? 0);
      setTimeout(() => refetch(), delay);
    },
  });

  const { broadcastState, markBroadcastRead, dismissBroadcast } =
    useBroadcastNotificationState({
      pruneAgainst: prune ? data?.userNotifications : undefined,
    });

  const visible = filterVisibleNotifications(
    applyBroadcastState(data?.userNotifications, broadcastState),
  );

  return {
    visible,
    loading,
    error,
    refetch,
    markBroadcastRead,
    dismissBroadcast,
  };
}
